import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../utils/api';

const StripeReturn = () => {
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState('loading'); // loading, complete, pending, error
    const [accountData, setAccountData] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const verifyAccount = async () => {
            try {
                // Get current user to find their accountId
                const meRes = await apiClient.get('/auth/me');
                const accountId = meRes.data.stripe_account_id;

                if (!accountId) {
                    setStatus('error');
                    return;
                }

                // Call backend to verify status
                const res = await apiClient.get(`/stripe/connect/account-status/${accountId}`);
                setAccountData(res.data);

                if (res.data.charges_enabled && res.data.payouts_enabled) {
                    setStatus('complete');
                } else {
                    setStatus('pending');
                }
            } catch (err) {
                console.error('Error verifying Stripe account:', err);
                setStatus('error');
            }
        };

        verifyAccount();
    }, []);

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
            <div className="max-w-md w-full bg-white rounded-3xl shadow-xl shadow-slate-200/50 p-8 text-center animate-in fade-in zoom-in duration-500">
                {status === 'loading' && (
                    <div className="flex flex-col items-center gap-4">
                        <div className="w-12 h-12 rounded-full border-4 border-indigo-500 border-t-transparent animate-spin"></div>
                        <h2 className="text-xl font-bold text-slate-900">Verifying your account...</h2>
                        <p className="text-slate-500">Please wait while we sync with Stripe.</p>
                    </div>
                )}

                {status === 'complete' && (
                    <div className="flex flex-col items-center gap-4">
                        <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-3xl mb-2">✅</div>
                        <h2 className="text-2xl font-bold text-slate-900">Stripe Connected!</h2>
                        <p className="text-slate-500">Your account is fully set up. You can now start accepting online payments from your clients.</p>
                        <button 
                            onClick={() => navigate('/dashboard')}
                            className="mt-4 w-full py-3 bg-indigo-600 text-white font-bold rounded-2xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200"
                        >
                            Go to Dashboard
                        </button>
                    </div>
                )}

                {status === 'pending' && (
                    <div className="flex flex-col items-center gap-4">
                        <div className="w-16 h-16 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center text-3xl mb-2">⏳</div>
                        <h2 className="text-2xl font-bold text-slate-900">Setup Incomplete</h2>
                        <p className="text-slate-500">Your Stripe setup is still pending some information. Please complete the onboarding to start accepting payments.</p>
                        <div className="w-full bg-slate-50 rounded-2xl p-4 text-left border border-slate-100">
                            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Requirements</h3>
                            <ul className="text-sm text-slate-600 space-y-1">
                                {accountData?.requirements?.currently_due?.map((req, i) => (
                                    <li key={i} className="flex items-center gap-2">
                                        <span className="text-amber-500">•</span> {req.replace(/_/g, ' ')}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <button 
                            onClick={() => navigate('/settings')}
                            className="mt-4 w-full py-3 bg-slate-900 text-white font-bold rounded-2xl hover:bg-slate-800 transition-all"
                        >
                            Finish Setup in Settings
                        </button>
                    </div>
                )}

                {status === 'error' && (
                    <div className="flex flex-col items-center gap-4">
                        <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-3xl mb-2">❌</div>
                        <h2 className="text-2xl font-bold text-slate-900">Connection Failed</h2>
                        <p className="text-slate-500">We couldn't verify your Stripe connection. This might be due to a network error or an expired session.</p>
                        <button 
                            onClick={() => navigate('/settings')}
                            className="mt-4 w-full py-3 bg-indigo-600 text-white font-bold rounded-2xl hover:bg-indigo-700 transition-all"
                        >
                            Back to Settings
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StripeReturn;
